import DataTable from "app/components/DataTable";
import { CheckedIcon, CheckboxOutlineIcon } from "app/icons";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ManufacturerModelRow } from "../../types";
import { useStyles } from "./styles";

type Props = {
  rows: ManufacturerModelRow[];
  loading?: boolean;
};

const CHECKBOX_SIZE = 20;

const renderCheckbox = (checked: boolean, styles: ReturnType<typeof useStyles>) => (
  <span className={styles.checkboxCell}>
    {checked ? (
      <CheckedIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />
    ) : (
      <CheckboxOutlineIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />
    )}
  </span>
);

const formatCellValue = (value: string | number | null): string | number => {
  if (value === null || value === "") {
    return "-";
  }

  return value;
};

const renderValue = (
  value: string | number | null,
  styles: ReturnType<typeof useStyles>
) => (
  <span className={styles.textCell}>{formatCellValue(value)}</span>
);

const ModelTable = ({ rows, loading = false }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        id: "manufacturer",
        label: t("MANUFACTURER_MODELS.TABLE.MANUFACTURER"),
        align: "center",
        minWidth: 100,
        format: (row: ManufacturerModelRow) => (
          <span className={styles.textCell}>{t(row.manufacturerKey)}</span>
        ),
      },
      {
        id: "model",
        label: t("MANUFACTURER_MODELS.TABLE.MODEL"),
        align: "center",
        minWidth: 180,
        format: (row: ManufacturerModelRow) => renderValue(row.model, styles),
      },
      {
        id: "year",
        label: t("MANUFACTURER_MODELS.TABLE.YEAR"),
        align: "center",
        minWidth: 72,
        format: (row: ManufacturerModelRow) => renderValue(row.year, styles),
      },
      {
        id: "chassisWidth",
        label: t("MANUFACTURER_MODELS.TABLE.CHASSIS_WIDTH"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) =>
          renderValue(row.chassisWidth, styles),
      },
      {
        id: "openingBase",
        label: t("MANUFACTURER_MODELS.TABLE.OPENING_BASE"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) =>
          renderValue(row.openingBase, styles),
      },
      {
        id: "trackWidth",
        label: t("MANUFACTURER_MODELS.TABLE.TRACK_WIDTH"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) =>
          renderValue(row.trackWidth, styles),
      },
      {
        id: "frontOverhang",
        label: t("MANUFACTURER_MODELS.TABLE.FRONT_OVERHANG"),
        align: "center",
        minWidth: 110,
        format: (row: ManufacturerModelRow) =>
          renderValue(row.frontOverhang, styles),
      },
      {
        id: "licensedWeight",
        label: t("MANUFACTURER_MODELS.TABLE.LICENSED_WEIGHT"),
        align: "center",
        minWidth: 110,
        format: (row: ManufacturerModelRow) =>
          renderValue(row.licensedWeight, styles),
      },
      {
        id: "drive",
        label: t("MANUFACTURER_MODELS.TABLE.DRIVE"),
        align: "center",
        minWidth: 72,
        format: (row: ManufacturerModelRow) => renderValue(row.drive, styles),
      },
      {
        id: "wheelbase",
        label: t("MANUFACTURER_MODELS.TABLE.WHEELBASE"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) =>
          renderValue(row.wheelbase, styles),
      },
      {
        id: "chassisWeight",
        label: t("MANUFACTURER_MODELS.TABLE.CHASSIS_WEIGHT"),
        align: "center",
        minWidth: 110,
        format: (row: ManufacturerModelRow) =>
          renderValue(row.chassisWeight, styles),
      },
      {
        id: "frontAxleDistance",
        label: t("MANUFACTURER_MODELS.TABLE.FRONT_AXLE_DISTANCE"),
        align: "center",
        minWidth: 140,
        format: (row: ManufacturerModelRow) =>
          renderValue(row.frontAxleDistance, styles),
      },
      {
        id: "sideLighting",
        label: t("MANUFACTURER_MODELS.TABLE.SIDE_LIGHTING"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) =>
          renderCheckbox(row.sideLighting, styles),
      },
      {
        id: "sideGuard",
        label: t("MANUFACTURER_MODELS.TABLE.SIDE_GUARD"),
        align: "center",
        minWidth: 88,
        format: (row: ManufacturerModelRow) =>
          renderCheckbox(row.sideGuard, styles),
      },
      {
        id: "mudguard",
        label: t("MANUFACTURER_MODELS.TABLE.MUDGUARD"),
        align: "center",
        minWidth: 88,
        format: (row: ManufacturerModelRow) =>
          renderCheckbox(row.mudguard, styles),
      },
      {
        id: "extendableChassis",
        label: t("MANUFACTURER_MODELS.TABLE.EXTENDABLE_CHASSIS"),
        align: "center",
        minWidth: 120,
        format: (row: ManufacturerModelRow) =>
          renderCheckbox(row.extendableChassis, styles),
      },
    ],
    [styles, t]
  );

  return (
    <div className={styles.root} dir="rtl">
      <DataTable
        rows={rows}
        columns={columns}
        scrollClass="custom-scroll-bar"
        loading={loading && rows.length === 0}
        serialEnabled={false}
        isPointerCursor
        noDataLabel={t("MANUFACTURER_MODELS.EMPTY")}
        onRowClick={(row: ManufacturerModelRow) =>
          navigate(`/system-tables/model-per-manufacturer/${row.id}`)
        }
      />
    </div>
  );
};

export default ModelTable;
